// Google Drive connect: the popup's only job is to hand Google's answer back to
// the window that opened it.
//
// Google returns the popup to /userProfile?code=…&scope=…&iss=… (the redirect
// URI the backend registers). The relay used to live in GoogleLoginButton's
// useEffect, which meant the popup had to render the WHOLE Settings page first —
// and an effect only runs after a render that succeeded. Any render error on
// that page sent the popup to the error boundary ("Oops! Something Went
// Wrong") with the code never relayed, so the drive could not be connected at
// all. index.js now calls this before React mounts, and the popup never renders
// the app.

export const GOOGLE_OAUTH_MESSAGE = 'bh:google-oauth';

/**
 * Reads Google's OAuth return off a search string.
 *
 * @param {string} search - `window.location.search`.
 * @returns {{code: string} | {error: string} | null} null when this is not a
 *   Google OAuth return.
 */
export const readGoogleOAuthReturn = (search) => {
  const params = new URLSearchParams(search || '');
  // A code is relayed as it always was. Nothing else this app receives carries
  // one (Acumatica's return lands as ?erp=…).
  const code = params.get('code');
  if (code) return { code };
  // A refusal (?error=access_denied) is only taken as Google's when it carries
  // Google's own markers, since `error` is a generic name.
  const error = params.get('error');
  const iss = params.get('iss') || '';
  const scope = params.get('scope') || '';
  const fromGoogle = iss.includes('accounts.google.com') || scope.includes('googleapis.com');
  return error && fromGoogle ? { error } : null;
};

/**
 * If this window is the Google OAuth popup coming back, post the result to the
 * opener and close. Returns true when it handled the page (the caller must then
 * not render the app).
 *
 * Without an opener (the popup was opened some other way, or the browser
 * severed it) there is nobody to tell, so it returns false and the app renders
 * as it always has.
 */
export const relayGoogleOAuthReturn = (win = window) => {
  const result = readGoogleOAuthReturn(win.location?.search);
  if (!result || !win.opener || win.opener.closed) return false;
  try {
    // Our own origin only: the code is a credential, and '*' would hand it to
    // whatever page happened to be the opener.
    win.opener.postMessage({ type: GOOGLE_OAUTH_MESSAGE, ...result }, win.location.origin);
  } catch (e) {
    return false;
  }
  win.close();
  return true;
};
